import { Observable, throwError } from 'rxjs';

import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';

import { TvLoginStore } from '../../pages/app/services/app.store';
import { EErrorCode } from '../entities/error-code';
import { NavigationService } from '../services/navigation.service';
import { IS_USER_AUTHORIZED, ITermsContext } from './terms.context';

@Injectable({
  providedIn: 'root',
})
export class Interceptor implements HttpInterceptor {
  private readonly tvLoginStore = inject(TvLoginStore);
  private readonly navigationService = inject(NavigationService);
  private readonly requestTokenName = 'X-XSRF-TOKEN';

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.tvLoginStore.getCSRFToken();
    const updatedReq = token ? this.getUpdatedRequestWithToken(req, token) : req;

    return next.handle(updatedReq).pipe(
      catchError((response) => {
        const error: Observable<never> = throwError(() => response);

        if (response instanceof HttpErrorResponse && response.status in EErrorCode) {
          const customErrorCode: number = response?.error?.errorCode
            ? +`${response.status}${response.error.errorCode}`
            : -1;

          const termsContext: ITermsContext = req.context.get(IS_USER_AUTHORIZED);

          if (this.shouldLogoutUser(termsContext, response, customErrorCode)) {
            this.navigationService.logoutByRedirect(termsContext.clientId);
            return error;
          }

          if (response.status === EErrorCode.Error_401_server_error) {
            this.navigationService.goToLogin();
            return error;
          }

          if (response.status === EErrorCode.Error_400_server_error) {
            if (customErrorCode in EErrorCode) {
              this.navigationService.goToErrorPage(customErrorCode);
              return error;
            }

            return error;
          }

          this.navigationService.goToErrorPage(response.status);
          return error;
        }

        if (response.error && typeof response.error !== 'object') {
          try {
            response.error = JSON.parse(response.error);
          } catch (e) {
            console.warn('cannot parse', response.error);
            this.navigationService.goToErrorPage(response.status);
            return error;
          }
        }

        return error;
      })
    );
  }

  private shouldLogoutUser(termsContext: ITermsContext, response: HttpErrorResponse, customErrorCode: number) {
    return (
      termsContext.userAuthorized &&
      response.status !== EErrorCode.Error_0_offline &&
      customErrorCode !== EErrorCode.Error_TC_invalid_version
    );
  }

  getUpdatedRequestWithToken(req, token) {
    const headers = req.headers.set(this.requestTokenName, token);
    return req.clone({ headers });
  }
}
